import React, { useEffect, useRef, useState } from 'react';
import { EditorState, convertFromHTML, ContentState, convertToRaw } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import draftToHtml from 'draftjs-to-html';

/**
 * `toolbar` names the control set, and every caller states its own — there is no default on
 * purpose. Each editor is paired with a server-side sanitizer that decides which markup actually
 * survives, so the caller is the only place that knows which controls are real: the LED marketing
 * editor drops fontFamily and colorPicker because MarketingHtml strips them, and the FAQ editor
 * does the same because FaqHtml does. A control whose choice is silently discarded on save is
 * worse than no control at all, and a shared default is how one editor's allowlist quietly
 * becomes another's.
 *
 * `paragraphs` makes Enter start a new paragraph instead of a line break inside the current one.
 * Defaults to the existing single-block behaviour.
 */
export default function HtmlEditorWithValue({
    value,
    onHtmlEditorStateChange,
    toolbar,
    paragraphs = false,
}) {
    const [editorState, setEditorState] = useState(() => {
        if (value) {
            const blocksFromHTML = convertFromHTML(value);
            const contentState = ContentState.createFromBlockArray(
                blocksFromHTML.contentBlocks,
                blocksFromHTML.entityMap
            );
            return EditorState.createWithContent(contentState);
        }
        return EditorState.createEmpty();
    });

    const [mounted, setMounted] = useState(false);
    const editorRef = useRef(null);

    useEffect(() => {
        setMounted(true);
    }, []);

    useEffect(() => {
        if (mounted && editorRef.current) {
            setTimeout(() => {
                try {
                    editorRef.current.focusEditor();
                } catch (e) {
                    // ignore if it fails
                }
            }, 100);
        }
    }, [mounted]);

    const handleEditorStateChange = (newState) => {
        setEditorState(newState);
        const html = draftToHtml(convertToRaw(newState.getCurrentContent()));
        onHtmlEditorStateChange(html);
    };

    const editorProps = {
        ref: editorRef,
        editorState,
        onEditorStateChange: handleEditorStateChange,
        toolbar,
    };

    if (!paragraphs) {
        editorProps.handleReturn = (e) => {
            e.preventDefault();
            document.execCommand('insertLineBreak');
            return true;
        };
    }

    return <Editor {...editorProps} />;
}
